import React from "react";
import { Theme, alpha } from "@mui/material/styles";
import { Box, Button, Dialog, Paper, Tab, Tabs, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { makeStyles } from "tss-react/mui";
import { keyframes } from "tss-react";

// Building blocks of the interactive documentation pages: header, sections, link cards, screenshots, animated flows

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 95, 152, .45); }
  100% { box-shadow: 0 0 0 12px rgba(0, 95, 152, 0); }
`;

const useStyles = makeStyles()((theme: Theme) => ({
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  stats: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  stat: {
    minWidth: 92,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 10,
    background: alpha(theme.palette.primary.main, 0.06),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    "& b": { display: "block", fontSize: 20, lineHeight: 1.2, color: theme.palette.primary.main },
    "& span": { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: theme.palette.text.secondary },
  },
  section: { marginTop: theme.spacing(4) },
  sectionHead: { display: "flex", alignItems: "baseline", gap: theme.spacing(1.5), marginBottom: theme.spacing(1.5), flexWrap: "wrap" },
  links: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: theme.spacing(1.5) },
  link: {
    display: "block",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: "4px solid",
    textDecoration: "none",
    color: "inherit",
    transition: "transform .15s, box-shadow .15s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: theme.shadows[3] },
    "& b": { display: "block", fontSize: 14 },
    "& span": { fontSize: 12.5, color: theme.palette.text.secondary },
  },
  shot: {
    position: "relative",
    margin: theme.spacing(1, 0),
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    cursor: "zoom-in",
    maxWidth: 560,
    "& img": { display: "block", width: "100%", height: "auto" },
    "& figcaption": { fontSize: 12, padding: theme.spacing(0.5, 1), color: theme.palette.text.secondary, background: theme.palette.background.default },
  },
  zoom: { position: "absolute", top: 6, right: 6, color: "#fff", background: "rgba(0,0,0,.45)", borderRadius: 6, padding: 2 },
  flow: {
    display: "grid",
    gap: theme.spacing(1),
    alignItems: "stretch",
    margin: theme.spacing(1.5, 0),
  },
  actor: {
    position: "relative",
    padding: theme.spacing(1.25),
    borderRadius: 10,
    border: `2px solid ${theme.palette.divider}`,
    textAlign: "center",
    fontSize: 13,
    transition: "all .25s",
    background: theme.palette.background.paper,
    "& b": { display: "block", fontSize: 14 },
    "& small": { color: theme.palette.text.secondary },
  },
  actorActive: { animation: `${pulse} 1.2s infinite`, transform: "translateY(-3px)" },
  message: {
    minHeight: 64,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: 13.5,
    lineHeight: 1.5,
    background: alpha(theme.palette.primary.main, 0.06),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  },
  dots: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  dot: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    color: "#fff",
  },
}));

export const PageHeader: React.FunctionComponent<{
  title: string;
  subtitle?: React.ReactNode;
  stats?: { label: string; value: number | string }[];
}> = ({ title, subtitle, stats }) => {
  const { classes } = useStyles();
  return (
    <div className={classes.header}>
      <div>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </div>
      {stats && (
        <div className={classes.stats}>
          {stats.map((s) => (
            <div key={s.label} className={classes.stat}>
              <b>{s.value}</b>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Section: React.FunctionComponent<{ id?: string; title: string; text?: React.ReactNode; children: React.ReactNode }> = ({
  id,
  title,
  text,
  children,
}) => {
  const { classes } = useStyles();
  return (
    <section id={id} className={classes.section}>
      <div className={classes.sectionHead}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {text && (
          <Typography variant="body2" color="text.secondary">
            {text}
          </Typography>
        )}
      </div>
      {children}
    </section>
  );
};

export interface DocLink {
  title: string;
  text?: string;
  url: string;
  color?: string;
}

export const LinkCards: React.FunctionComponent<{ links: DocLink[] }> = ({ links }) => {
  const { classes } = useStyles();
  return (
    <div className={classes.links}>
      {links.map((l) => (
        <a key={l.title} className={classes.link} href={l.url} target="_blank" rel="noreferrer" style={{ borderLeftColor: l.color ?? "#005f98" }}>
          <b>{l.title} ↗</b>
          {l.text && <span>{l.text}</span>}
        </a>
      ))}
    </div>
  );
};

/** Screenshot from /public; click to open it full size */
export const Screenshot: React.FunctionComponent<{ src: string; caption: string }> = ({ src, caption }) => {
  const { classes } = useStyles();
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <figure className={classes.shot} style={{ marginLeft: 0 }} onClick={() => setOpen(true)}>
        <img src={`${process.env.PUBLIC_URL}${src}`} alt={caption} loading="lazy" />
        <ZoomInIcon className={classes.zoom} fontSize="small" />
        <figcaption>{caption}</figcaption>
      </figure>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl">
        <img src={`${process.env.PUBLIC_URL}${src}`} alt={caption} style={{ display: "block", maxWidth: "100%", cursor: "zoom-out" }} onClick={() => setOpen(false)} />
      </Dialog>
    </>
  );
};

export interface FlowActor {
  id: string;
  name: string;
  sub?: string;
  color: string;
}

export interface FlowStep {
  from: string;
  to: string;
  text: React.ReactNode;
}

/** Sequence of messages between actors; plays automatically or step by step */
export const Flow: React.FunctionComponent<{ actors: FlowActor[]; steps: FlowStep[] }> = ({ actors, steps }) => {
  const { classes, cx } = useStyles();
  const [step, setStep] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  React.useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => setStep((s) => (s + 1) % steps.length), 2600);
    return () => clearInterval(timer);
  }, [playing, steps.length]);
  React.useEffect(() => setStep(0), [steps]);
  const current = steps[Math.min(step, steps.length - 1)];
  const fromIndex = actors.findIndex((a) => a.id === current.from);
  const toIndex = actors.findIndex((a) => a.id === current.to);
  const color = actors[fromIndex]?.color ?? "#005f98";

  return (
    <Box>
      <div className={classes.flow} style={{ gridTemplateColumns: `repeat(${actors.length}, minmax(0, 1fr))` }}>
        {actors.map((a, i) => (
          <div
            key={a.id}
            className={cx(classes.actor, { [classes.actorActive]: i === fromIndex || i === toIndex })}
            style={{ borderColor: i === fromIndex || i === toIndex ? a.color : undefined, background: i === fromIndex || i === toIndex ? alpha(a.color, 0.08) : undefined }}
          >
            <b style={{ color: a.color }}>{a.name}</b>
            {a.sub && <small>{a.sub}</small>}
          </div>
        ))}
      </div>
      <Box sx={{ position: "relative", height: 18, mb: 1 }}>
        {fromIndex >= 0 && toIndex >= 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 7,
              height: 4,
              borderRadius: 2,
              background: color,
              transition: "all .35s",
              left: `${((Math.min(fromIndex, toIndex) + 0.5) / actors.length) * 100}%`,
              width: `${(Math.max(Math.abs(toIndex - fromIndex), 0.15) / actors.length) * 100}%`,
            }}
          >
            <Box
              component="span"
              sx={{
                position: "absolute",
                top: -8,
                [toIndex >= fromIndex ? "right" : "left"]: -6,
                fontSize: 16,
                lineHeight: 1,
                color,
              }}
            >
              {toIndex >= fromIndex ? "▶" : "◀"}
            </Box>
          </Box>
        )}
      </Box>
      <Paper variant="outlined" className={classes.message}>
        <b>
          {step + 1}/{steps.length} · {actors[fromIndex]?.name} → {actors[toIndex]?.name}:{" "}
        </b>
        {current.text}
      </Paper>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, flexWrap: "wrap" }}>
        <Button size="small" variant="outlined" startIcon={playing ? <PauseIcon /> : <PlayArrowIcon />} onClick={() => setPlaying((p) => !p)}>
          {playing ? "Pause" : "Play"}
        </Button>
        <Button size="small" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
          Back
        </Button>
        <Button size="small" disabled={step === steps.length - 1} onClick={() => setStep((s) => s + 1)}>
          Next
        </Button>
        <div className={classes.dots}>
          {steps.map((s, i) => (
            <button
              key={i}
              className={classes.dot}
              style={{ background: i === step ? actors.find((a) => a.id === s.from)?.color : "#b0bec5" }}
              onClick={() => {
                setPlaying(false);
                setStep(i);
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </Box>
    </Box>
  );
};

/** Tabs with content panels; the tab can be preselected (e.g. from the route) */
export const DocTabs: React.FunctionComponent<{ tabs: { id: string; label: string; content: React.ReactNode }[]; initial?: string }> = ({ tabs, initial }) => {
  const [tab, setTab] = React.useState(initial && tabs.some((t) => t.id === initial) ? initial : tabs[0].id);
  React.useEffect(() => {
    if (initial) setTab(initial);
  }, [initial]);
  return (
    <>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { textTransform: "none", fontWeight: 600 } }}
      >
        {tabs.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} />
        ))}
      </Tabs>
      <Paper variant="outlined" sx={{ p: 2.5, mt: 1.5, borderRadius: 3 }}>
        {tabs.find((t) => t.id === tab)?.content}
      </Paper>
    </>
  );
};
